/**
 * Task D3 — the per-item outcome of one job. **This is what an operator reads
 * after a `PARTIAL`** (§05.8, criterion 20), so it is the screen that has to
 * answer "which of my SIMs did not work, and why" without making the reader
 * cross-reference anything.
 *
 * Two rules it exists to hold:
 *
 * **The MSISDN is rendered in full.** It is the customer's own identifier (§7),
 * and an operator matching a row against a SIM in their hand or a ticket cannot
 * do it against `••••1234`. Masking or truncating it is a defect, not a privacy
 * measure — criterion 6 asserts byte-identity with the fixture.
 *
 * **The reason is the normalised one.** `ClientJobOrder.reason` is already the
 * API's normalised text; the mapper boundary (§6.1) is where a vendor's error
 * string stops. This component renders what it is given and invents no copy of
 * its own, because a client-side translation table would be a second
 * vocabulary that drifts from the server's.
 */

import type { ClientJobOrder } from '@rd/shared';
import type { ReactElement } from 'react';

import { Badge, type BadgeTone } from '../../components/Badge.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Pagination } from '../../components/Pagination.js';
import { Table, type Column } from '../../components/Table.js';
import { usePaginatedQuery } from '../../lib/paginated.js';

/**
 * The API normalises a line item to one of four values
 * (`apps/api/src/modules/jobs/jobs.schema.ts`). An unrecognised value falls
 * through to `neutral` rather than being guessed at: a confidently wrong colour
 * on a row an operator is about to act on is worse than an uncoloured one.
 */
const ORDER_TONE: Record<string, BadgeTone> = {
  PENDING: 'progress',
  SUCCESS: 'success',
  FAILED: 'error',
  REJECTED: 'error',
};

/**
 * A line item plus a key that is guaranteed unique.
 *
 * The API serves line items from the upstream while a job is live, and those
 * carry an empty `ref` until they have been persisted
 * (`jobs.service.ts#toClientOrderFromDomain`). Keying the table rows on `ref`
 * directly would collide every row of a running job onto one React key.
 */
interface OrderRow extends ClientJobOrder {
  rowKey: string;
}

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

export function JobOrdersTable({ jobRef }: { jobRef: string }): ReactElement {
  const query = usePaginatedQuery<ClientJobOrder>({
    key: ['job-orders', jobRef],
    path: `/jobs/${jobRef}/orders`,
    enabled: jobRef !== '',
  });

  const rows: OrderRow[] = (query.data?.data ?? []).map((order, index) => ({
    ...order,
    rowKey: order.ref === '' ? `${order.msisdn ?? 'item'}:${String(index)}` : order.ref,
  }));

  const columns: Column<OrderRow>[] = [
    {
      key: 'msisdn',
      header: 'MSISDN',
      // In full, and through the table's own `mono` kind rather than a local
      // `font-mono` — so the identifiers on this screen are set exactly as the
      // identifiers in the estate table are (tabular figures,
      // `--tracking-ident`, no wrapping). An operator reads the two against
      // each other; two spacings of the same number is a defect they would
      // have to compensate for by eye.
      kind: 'mono',
      render: (row) => row.msisdn ?? '—',
    },
    {
      key: 'iccid',
      header: 'ICCID',
      kind: 'mono',
      render: (row) => row.iccid ?? '—',
    },
    {
      key: 'status',
      header: 'Outcome',
      kind: 'status',
      width: '8.5rem',
      // `sm`: the pill sits inside a table row, where a control-sized one reads
      // as something an operator can press.
      render: (row) => (
        <Badge size="sm" tone={ORDER_TONE[row.status.toUpperCase()] ?? 'neutral'}>
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => <span className="text-body">{row.reason ?? '—'}</span>,
    },
    {
      key: 'completedAt',
      header: 'Completed',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums text-muted">
          {formatWhen(row.completedAt)}
        </span>
      ),
    },
  ];

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <>
      {/*
        The table's own skeleton, not a stack of loose bars where the table
        will be. The header stays up, so the columns still say what is coming,
        and the placeholder cells are the widths the real columns will be — so
        the reader's eye is already in the right place when the rows land.
      */}
      <Table
        caption="Every SIM in this job, with its outcome"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.rowKey}
        loading={query.isPending}
        empty={
          <EmptyState
            title="No items to show"
            body="This job has no line items recorded against it yet."
          />
        }
      />
      {query.data ? (
        <Pagination meta={query.data.meta} onPageChange={query.setPage} label="Job items" />
      ) : null}
    </>
  );
}
